import { CancelDispatchErrorModal } from "../components/cancel-dispatch-error-modal/index.tsx";
import { CancelDispatchModal } from "../components/cancel-dispatch-modal/index.tsx";
import { CancelShipmentModal } from "../components/cancel-shipment-modal/index.tsx";
import { ClaimsModal } from "../components/claims-modal/index.tsx";
import { CreateAttentionFlagModal } from "../components/create-flag-modal/index.tsx";
import { AdditionalDetailsModal } from "../components/details-modal/index.tsx";
import { DiscardEditsModal } from "../components/discard-edits-modal/index.tsx";
import { ExceptionModal } from "../components/exception-modal/exception-modal.tsx";
import { ForceUnlockModal } from "../components/force-unlock-modal/index.tsx";
import { SaveChangesModal } from "../components/save-on-exit-modal/index.tsx";
import type { AugmentedShipment } from "../types/augmented-shipment";

export type ExceptionType =
	| "immediateActionExceptions"
	| "needsAcknowledgedExceptions";

export type ActiveModal =
	| null
	| { type: "additionalDetails" }
	| { type: "cancelDispatch" }
	| { type: "cancelDispatchError" }
	| { type: "cancelDispatchPrompt" }
	| { type: "cancelShipment" }
	| { type: "claims" }
	| { type: "createFlag" }
	| { type: "discardEdits" }
	| { type: "exception"; exceptionType: ExceptionType }
	| { type: "forceUnlock" }
	| { type: "saveChanges" };

export const ShipmentActionModals = ({
	activeModal,
	onClose,
	forceUnlock,
	onStayOnPage,
	onDiscardAndLeave,
	onDiscardEdits,
	onSaveAndLeave,
	shipment,
	rerateRequired,
	isSaving,
}: {
	activeModal: ActiveModal;
	onClose: () => void;
	forceUnlock: () => void;
	onStayOnPage: () => void;
	onDiscardAndLeave: () => void;
	onDiscardEdits: () => void;
	onSaveAndLeave: () => void;
	shipment: AugmentedShipment;
	rerateRequired: boolean;
	isSaving: boolean;
}) => {
	switch (activeModal?.type) {
		case "exception": {
			return (
				<ExceptionModal
					exceptionType={activeModal.exceptionType}
					isOpen={true}
					onClose={onClose}
					shipment={shipment}
				/>
			);
		}
		case "cancelDispatch": {
			return <CancelDispatchModal isOpen={true} onClose={onClose} />;
		}
		case "cancelShipment": {
			return (
				<CancelShipmentModal
					carrierPhone={shipment.carrier?.contactInfo?.phone}
					isOpen={true}
					onClose={onClose}
				/>
			);
		}
		case "cancelDispatchError": {
			return (
				<CancelDispatchErrorModal
					carrierPhone={shipment.carrier?.contactInfo?.phone}
					confirmationNumber={shipment.carrier?.confirmationNumber}
					isOpen={true}
					onClose={onClose}
				/>
			);
		}
		case "forceUnlock": {
			return (
				<ForceUnlockModal
					forceUnlock={forceUnlock}
					isOpen={true}
					onClose={onClose}
				/>
			);
		}
		case "additionalDetails": {
			return (
				<AdditionalDetailsModal
					details={shipment.shipmentDetails?.additionalDetails ?? undefined}
					isOpen={true}
					onClose={onClose}
					quoteId={shipment.quoteId}
				/>
			);
		}
		case "createFlag": {
			return (
				<CreateAttentionFlagModal
					isOpen={true}
					onClose={onClose}
					shipment={shipment}
				/>
			);
		}
		case "claims": {
			return (
				<ClaimsModal isOpen={true} onClose={onClose} shipment={shipment} />
			);
		}
		case "saveChanges": {
			return (
				<SaveChangesModal
					isOpen={true}
					isSaving={isSaving}
					onDiscardAndLeave={onDiscardAndLeave}
					onSaveAndLeave={onSaveAndLeave}
					onStay={onStayOnPage}
					rerateRequired={rerateRequired}
				/>
			);
		}
		case "discardEdits": {
			return (
				<DiscardEditsModal
					isOpen={true}
					onDiscard={onDiscardEdits}
					onKeepEditing={onClose}
				/>
			);
		}
		case "cancelDispatchPrompt":
		case undefined: {
			return null;
		}
	}
};
